/**
 * SiteSwitcher — the scope control, in the title bar.
 *
 * This is the most consequential control in the app: it decides what every menu entry and every
 * page below it means. It used to sit mid-sidebar, styled like the rows it governs, with its
 * label scrolling out of view — so operators inferred scope from position and got it wrong. Here
 * it is permanently on screen, above everything it scopes.
 *
 * "All Sites" is the first row and the default, not an absence. Picking it is choosing a scope
 * with its own menu, which is why there is no longer a "no site selected" empty state.
 *
 * Site status is shown BEFORE you commit to switching — which site is up, which is degraded —
 * because "where do I need to be" is the question that sends an operator to this control.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronUpDownIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  ServerStackIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useNode } from '../../contexts/NodeContext';
import { useAuth } from '../../contexts/AuthContext';
import { resolveScopeSwitch } from '../../constants/scope';

/** Show the filter box only once scanning the list is slower than typing. */
const SEARCH_THRESHOLD = 7;

export default function SiteSwitcher() {
  const { nodes, selectedNode, selectedNodeId, selectNode, loading } = useNode();
  const { user, setDefaultSite, hasPermission } = useAuth();
  /** Adding, renaming and removing sites — the Sites page, reached from here. */
  const canManageSites = hasPermission('nodes.view');
  /** The site a fresh session opens on. Lives on the ACCOUNT, so it follows the operator. */
  const favouriteId = user?.default_node_id ?? null;
  const [savingFavourite, setSavingFavourite] = useState<string | null>(null);
  const [favouriteError, setFavouriteError] = useState('');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else {
      setQuery('');
      setFavouriteError('');
    }
  }, [open]);

  /**
   * Star / un-star a site. Deliberately does NOT switch scope or close the menu: marking where
   * tomorrow's session should start is a different intent from going there now, and collapsing
   * the two would make one click do two things the operator did not both ask for.
   */
  const toggleFavourite = async (nodeId: string) => {
    const next = favouriteId === nodeId ? null : nodeId;
    setSavingFavourite(nodeId);
    setFavouriteError('');
    try {
      await setDefaultSite(next);
    } catch (err: any) {
      // Shown in the menu rather than as a toast: the control that failed is on screen, and
      // this app has no toast host. The server's message covers the real refusal — favouring
      // a site you have no grant for.
      setFavouriteError(err?.message || 'Could not save the default site');
    } finally {
      setSavingFavourite(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? nodes.filter((n) => n.name.toLowerCase().includes(q)) : nodes;
  }, [nodes, query]);

  /**
   * Change scope, then land the operator somewhere that still makes sense — see
   * resolveScopeSwitch. A null target means the current page survives the switch, which is the
   * answer for site → site and for every platform-admin page.
   */
  const switchTo = (nodeId: string | null) => {
    setOpen(false);
    if (selectedNodeId === nodeId) return;
    const target = resolveScopeSwitch(pathname, nodeId === null);
    selectNode(nodeId);
    if (target) navigate(target);
  };

  // The SCOPE, not the resolved node: while the node list loads, a stored site must not read
  // as All Sites in the one control whose whole job is saying which scope you are in.
  const allSites = !selectedNodeId;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-9 max-w-[240px] items-center gap-2 rounded-lg border border-border bg-background px-2.5 text-[13px] font-medium text-foreground transition-colors hover:bg-muted sm:max-w-[300px]"
        title="Change scope"
      >
        {allSites ? (
          <>
            <GlobeAltIcon className="h-4 w-4 flex-none text-brand" />
            <span className="truncate">All Sites</span>
            {nodes.length > 0 && (
              <span className="hidden flex-none text-[11px] font-normal text-muted-foreground sm:inline">
                {nodes.length} site{nodes.length === 1 ? '' : 's'}
              </span>
            )}
          </>
        ) : (
          <>
            {/* Mid-load the name isn't known yet. Show the scope as pending rather than
                guessing at All Sites, which would be the one wrong answer. */}
            <StatusDot status={selectedNode?.status ?? 'unknown'} />
            <span className="truncate">{selectedNode?.name ?? 'Loading site…'}</span>
            {selectedNode && selectedNode.status !== 'online' && (
              <span className="hidden flex-none rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive sm:inline">
                Offline
              </span>
            )}
          </>
        )}
        <ChevronUpDownIcon className="h-4 w-4 flex-none text-muted-foreground" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1.5 w-[300px] overflow-hidden rounded-lg border border-border bg-card shadow-xl"
        >
          {nodes.length >= SEARCH_THRESHOLD && (
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <MagnifyingGlassIcon className="h-4 w-4 flex-none text-muted-foreground" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a site…"
                className="w-full bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none"
              />
            </div>
          )}

          {/* A scope, not a site — separated so it doesn't read as one more row in the list. */}
          <button
            type="button"
            role="option"
            aria-selected={allSites}
            onClick={() => switchTo(null)}
            className={`flex w-full items-center gap-2.5 border-b border-border px-3 py-2.5 text-left text-[13px] transition-colors hover:bg-muted ${
              allSites ? 'bg-brand-light font-semibold text-brand' : 'text-foreground'
            }`}
          >
            <GlobeAltIcon className="h-4 w-4 flex-none" />
            <span className="flex-1">All Sites</span>
            <span className="flex-none text-[11px] text-muted-foreground">
              {nodes.length} site{nodes.length === 1 ? '' : 's'}
            </span>
          </button>

          <div className="max-h-[320px] overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {loading && nodes.length === 0 && (
              <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">Loading sites…</p>
            )}
            {!loading && nodes.length === 0 && (
              <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">No sites registered</p>
            )}
            {nodes.length > 0 && filtered.length === 0 && (
              <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">
                No site matches “{query}”
              </p>
            )}

            {filtered.map((node) => {
              const active = selectedNodeId === node.nodeId;
              const isFavourite = favouriteId === node.nodeId;
              return (
                <div
                  key={node.nodeId}
                  className={`group flex w-full items-center transition-colors hover:bg-muted ${
                    active ? 'bg-brand-light' : ''
                  }`}
                >
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => switchTo(node.nodeId)}
                  className={`flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-left text-[13px] ${
                    active ? 'font-semibold text-brand' : 'text-foreground'
                  }`}
                >
                  <StatusDot status={node.status} />
                  <span className="min-w-0 flex-1 truncate">{node.name}</span>
                  {/* Said in words as well as by the dot: switching to a site that is down gives
                      a menu that mostly can't be used, so the cost is worth signposting. */}
                  {node.status !== 'online' && (
                    <span className="flex-none rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive">
                      Offline
                    </span>
                  )}
                  {node.status === 'online' && node.tunnelActive && (
                    <span className="flex-none rounded bg-brand-light px-1.5 py-0.5 text-[10px] font-medium text-brand">
                      Tunnel
                    </span>
                  )}
                </button>
                {/* A filled star is always visible; an empty one appears on hover or keyboard
                    focus, so the row stays a list of sites rather than a column of icons. */}
                <button
                  type="button"
                  onClick={() => toggleFavourite(node.nodeId)}
                  disabled={savingFavourite === node.nodeId}
                  aria-pressed={isFavourite}
                  aria-label={
                    isFavourite
                      ? `${node.name} is your default site — remove it`
                      : `Make ${node.name} your default site`
                  }
                  title={
                    isFavourite
                      ? 'Default site — new sessions start here. Click to clear.'
                      : 'Make this my default site'
                  }
                  className={`flex-none px-2.5 py-2.5 transition-opacity disabled:opacity-40 ${
                    isFavourite
                      ? 'text-brand'
                      : 'text-muted-foreground opacity-0 hover:text-brand focus:opacity-100 group-hover:opacity-100'
                  }`}
                >
                  {isFavourite ? (
                    <StarIconSolid className="h-4 w-4" />
                  ) : (
                    <StarIcon className="h-4 w-4" />
                  )}
                </button>
                </div>
              );
            })}
          </div>

          {/* Not a scope: the page where sites themselves are added, edited and removed. Sits
              under the list it changes, apart from it, so it never reads as one more site. */}
          {canManageSites && (
            <button
              type="button"
              onClick={() => { setOpen(false); navigate('/sites'); }}
              className={`flex w-full items-center gap-2.5 border-t border-border px-3 py-2.5 text-left text-[13px] transition-colors hover:bg-muted ${
                pathname === '/sites' ? 'font-semibold text-brand' : 'text-foreground'
              }`}
            >
              <Cog6ToothIcon className="h-4 w-4 flex-none" />
              <span className="flex-1">Manage sites</span>
              <span className="flex-none text-[11px] text-muted-foreground">Add · edit · remove</span>
            </button>
          )}

          {favouriteError && (
            <p className="border-t border-border bg-destructive/10 px-3 py-1.5 text-[11px] text-destructive">
              {favouriteError}
            </p>
          )}

          <div className="flex flex-col gap-1 border-t border-border bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <ServerStackIcon className="h-3.5 w-3.5 flex-none" />
              Everything in the sidebar belongs to the scope selected here.
            </span>
            <span className="flex items-center gap-1.5">
              <StarIcon className="h-3.5 w-3.5 flex-none" />
              {favouriteId
                ? 'Starred site opens automatically when you sign in.'
                : 'Star a site to open it automatically when you sign in.'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const known = status === 'online' || status === 'offline';
  const online = status === 'online';
  return (
    <span
      aria-hidden
      title={known ? (online ? 'Online' : 'Offline') : 'Checking…'}
      className={`h-2.5 w-2.5 flex-none rounded-full ${
        !known ? 'bg-muted-foreground/40' : online ? 'bg-emerald-500' : 'bg-destructive'
      }`}
    />
  );
}
